import FilledButtonM from "@common/components/button/FilledButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { DigitsCodeInputM } from "@common/components/input/DigitsCodeInputM"
import TextInputM from "@common/components/input/TextInputM"
import ErrorTextM from "@common/components/text/ErrorTextM"
import TextM from "@common/components/text/TextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useInterval } from "@common/utils/useInterval"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { switchAccount } from "@data/redux/actions/authTokens"
import { setPendingDeepLink } from "@data/redux/actions/notifications"
import { fastTrackService } from "@data/services/fastTrackService"
import * as Device from "expo-device"
import * as Linking from "expo-linking"
import React, { useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Image, Keyboard, ScrollView, StyleSheet, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import logo from "../../../assets/logo-blue.png"
import { loginContentWidth } from "../../auth/loginLayout"
import { trackLoginSucceeded } from "@data/auth/loginSuccess"
import {
    RECOGNITION_OUTCOMES,
    channelLabel,
    childSpaceLabel,
    otherChannelLead,
    outcomeOfLanding,
    recognitionRequest,
    tokenDataFor,
    trackRecognition,
} from "./recognitionFlow"

const CODE_LENGTH = 6

const ChannelRow = ({ icon, title, subtitle, onPress, disabled }) => (
    <TouchableRippleM
        onPress={onPress}
        disabled={disabled}
        style={styles.channelRow}
    >
        <View style={styles.channelRowContent}>
            <FontAwesome5
                name={icon}
                solid
                size={18}
                color={theme.colors.primary}
            />
            <View style={{ flex: 1 }}>
                <TextM fontWeight="Bold" color={theme.colors.primary} wrap>
                    {title}
                </TextM>
                {!!subtitle && (
                    <TextM fontSize={13} color={theme.colors.grey} wrap>
                        {subtitle}
                    </TextM>
                )}
            </View>
            <FontAwesome5
                name="chevron-right"
                size={14}
                color={theme.colors.primary}
            />
        </View>
    </TouchableRippleM>
)

/**
 * « On vous connaît déjà » : the landing form named a family Masteur knows.
 * The family proves who it is with a code (or its password), then continues on
 * its child's account, adds the child to its family, or — when it has lost its
 * phone and email — goes on as before on a new account.
 *
 * `onLanded(initPayload)` hands the account back to the fast-track, which
 * continues exactly as after `fast-track/init`.
 */
const FastTrackRecognitionFlow = (props) => {
    // `trackedIntent`: what the Amplitude event calls the request (the form
    // key stands in for a missing lead_intent).
    const { recognition, trackedIntent, onLanded, navigation } = props
    const dimensions = useContext(DimensionsContext)
    // Same column as the login screens, so the two code steps look alike.
    const width = loginContentWidth(dimensions)
    const firstname = recognition?.firstname

    // choose | code | password | other_channel | new_child | pick | subscribed
    const [step, setStep] = useState("choose")
    const [channel, setChannel] = useState(null) // { channel, masked }
    const [login, setLogin] = useState(null)
    const [next, setNext] = useState(null)
    const [landing, setLanding] = useState(null)
    const [goingHome, setGoingHome] = useState(false)
    const [resendIn, setResendIn] = useState(0)

    const codeForm = useForm({ defaultValues: { code: "" } })
    const passwordForm = useForm({ defaultValues: { password: "" } })
    const code = codeForm.watch("code") ?? ""

    useInterval(
        () => setResendIn((seconds) => Math.max(0, seconds - 1)),
        resendIn > 0 ? 1000 : null
    )

    useEffect(() => {
        const services = [
            fastTrackService.sendRecognitionCode,
            fastTrackService.verifyRecognition,
            fastTrackService.resolveRecognition,
            fastTrackService.skipRecognition,
        ]
        services.forEach((service) => props.apiIdle(service))
    }, [])

    const sendCode = (target) => {
        if (props.sending) return
        props.apiIdle(fastTrackService.verifyRecognition)
        setChannel(target)
        props.apiRequest(
            fastTrackService.sendRecognitionCode,
            {},
            { token: recognition.token, channel: target.channel }
        )
    }

    useApiStatus(
        fastTrackService.sendRecognitionCode,
        null,
        false,
        (data) => {
            setResendIn(data?.resend_after ?? 60)
            codeForm.reset({ code: "" })
            setStep("code")
        },
        (error) => {
            // Asked again too soon (a reload, a second tab): the code already
            // sent still works, so the field is what the family needs.
            if (error?.resend_after) {
                setResendIn(error.resend_after)
                setStep("code")
            }
        }
    )

    const verify = (proof) => {
        if (props.verifying) return
        Keyboard.dismiss()
        props.apiRequest(
            fastTrackService.verifyRecognition,
            {},
            {
                token: recognition.token,
                device_name: Device.deviceName ?? "WEB",
                ...proof,
            }
        )
    }

    const submitCode = () => verify({ channel: channel.channel, code })

    const submitPassword = () =>
        verify({
            channel: "password",
            password: passwordForm.getValues("password"),
        })

    // Typing again clears a wrong-code error, not a dead code's.
    const clearVerifyError = () => {
        if (props.verifyError && !props.verifyError.expired)
            props.apiIdle(fastTrackService.verifyRecognition)
    }

    const resolve = (childId) =>
        props.apiRequest(
            fastTrackService.resolveRecognition,
            {},
            { token: recognition.token, child_id: childId ?? null }
        )

    useApiStatus(fastTrackService.verifyRecognition, null, false, (data) => {
        trackLoginSucceeded(data?.method, data)
        setLogin(data)
        const nextStep = data?.recognition
        setNext(nextStep)
        if (nextStep?.outcome === "known_child") {
            resolve(nextStep.child.id)
            return
        }
        setStep(
            nextStep?.outcome === "other_channel"
                ? "other_channel"
                : "new_child"
        )
    })

    useApiStatus(fastTrackService.resolveRecognition, null, false, (data) => {
        trackRecognition(outcomeOfLanding(data), trackedIntent)
        // A subscriber's trial or second subscription: its space, not a sale.
        if (data?.no_sale) {
            setLanding(data)
            setStep("subscribed")
            return
        }
        onLanded(data)
    })

    useApiStatus(fastTrackService.skipRecognition, null, false, (data) => {
        trackRecognition(RECOGNITION_OUTCOMES.SKIPPED, trackedIntent)
        onLanded(data)
    })

    // Pasting or autofilling the whole code logs in without an extra tap.
    useEffect(() => {
        if (
            step === "code" &&
            code.length === CODE_LENGTH &&
            !props.verifyError
        )
            submitCode()
    }, [code])

    const isInscription = recognition?.product?.key === "inscription"

    const goToSpace = () => {
        const tokenData = tokenDataFor(login, landing?.id)
        if (!tokenData) return
        // The subscription lives in the parent space: the root navigator
        // enters parent mode and opens it once the account is switched in.
        if (isInscription)
            props.setPendingDeepLink(Linking.createURL("parent-subscription"))
        props.switchAccount(tokenData)
        setGoingHome(true)
    }

    // The main screens only exist once the switch has landed.
    useEffect(() => {
        if (goingHome && props.sessionToken && !isInscription)
            navigation.replace("menu")
    }, [goingHome, props.sessionToken])

    const codeIsDead = !!props.verifyError?.expired
    const smsOrEmail = recognition?.channels ?? []

    const renderChoose = () => (
        <>
            <TextM fontWeight="Bold" style={styles.center} wrap>
                {recognitionRequest(recognition?.product?.label, firstname)}
            </TextM>
            <TextM style={styles.lead} wrap>
                Connectez-vous pour continuer.
            </TextM>
            <TextM fontWeight="Bold" style={styles.sectionTitle}>
                Recevoir un code de connexion
            </TextM>
            {smsOrEmail.map((target) => (
                <ChannelRow
                    key={target.channel}
                    icon={target.channel === "sms" ? "mobile-alt" : "envelope"}
                    title={target.channel === "sms" ? "Par SMS" : "Par email"}
                    subtitle={target.masked}
                    disabled={props.sending}
                    onPress={() => sendCode(target)}
                />
            ))}
            <ErrorTextM errorData={props.sendError} style={styles.center} />
            {recognition?.password && (
                <TextButtonM
                    style={{ marginTop: 14 }}
                    onPress={() => {
                        props.apiIdle(fastTrackService.verifyRecognition)
                        setStep("password")
                    }}
                    label="Utiliser mon mot de passe"
                />
            )}
            <TextButtonM
                style={{ marginTop: 28, fontSize: 13 }}
                color={theme.colors.grey}
                loading={props.skipping}
                onPress={() =>
                    props.apiRequest(
                        fastTrackService.skipRecognition,
                        {},
                        { token: recognition.token }
                    )
                }
                numberOfLines={2}
                label="Je n'ai plus accès à ce téléphone ni à cet email"
            />
            <ErrorTextM errorData={props.skipError} style={styles.center} />
        </>
    )

    const renderCode = () => (
        <>
            <TextM style={styles.lead} wrap>
                Un code vient d&apos;être envoyé{" "}
                <TextM fontWeight="Bold">{channelLabel(channel)}</TextM>.
            </TextM>
            <DigitsCodeInputM
                length={CODE_LENGTH}
                control={codeForm.control}
                setValue={codeForm.setValue}
                width={width}
                placeholder="Code à 6 chiffres"
                accessibilityLabel="Code à 6 chiffres"
                autoFocus
                editable={!codeIsDead}
                onChangeAdditional={clearVerifyError}
                onSubmitEditing={submitCode}
            />
            <ErrorTextM errorData={props.verifyError} style={styles.center} />
            <ErrorTextM errorData={props.resolveError} style={styles.center} />
            <FilledButtonM
                style={{ marginTop: 20, width }}
                disabled={codeIsDead || code.length !== CODE_LENGTH}
                loading={props.verifying || props.resolving}
                onPress={submitCode}
                label="Me connecter"
            />
            <TextButtonM
                style={{ marginTop: 16 }}
                disabled={resendIn > 0 || props.sending}
                onPress={() => sendCode(channel)}
                label={
                    resendIn > 0
                        ? `Renvoyer le code (${resendIn} s)`
                        : "Renvoyer le code"
                }
            />
            <ErrorTextM errorData={props.sendError} style={styles.center} />
            <TextButtonM
                style={{ marginTop: 16 }}
                color={theme.colors.grey}
                onPress={() => setStep("choose")}
                label="Choisir un autre moyen"
            />
        </>
    )

    const renderPassword = () => (
        <>
            <TextM style={styles.lead} wrap>
                Le mot de passe du compte{" "}
                <TextM fontWeight="Bold">
                    {smsOrEmail.find((c) => c.channel === "email")?.masked}
                </TextM>
                .
            </TextM>
            <TextInputM
                placeholder="Mot de passe"
                name="password"
                control={passwordForm.control}
                password
                width={width}
                onChangeAdditional={clearVerifyError}
                onSubmitEditing={submitPassword}
            />
            <ErrorTextM errorData={props.verifyError} style={styles.center} />
            <ErrorTextM errorData={props.resolveError} style={styles.center} />
            <FilledButtonM
                style={{ marginTop: 20, width }}
                disabled={!passwordForm.watch("password") || codeIsDead}
                loading={props.verifying || props.resolving}
                onPress={submitPassword}
                label="Me connecter"
            />
            <TextButtonM
                style={{ marginTop: 16 }}
                color={theme.colors.grey}
                onPress={() => setStep("choose")}
                label="Recevoir plutôt un code"
            />
        </>
    )

    const renderOtherChannel = () => (
        <>
            <TextM style={styles.lead} wrap>
                {otherChannelLead(firstname, channelLabel(next.other_channel))}
            </TextM>
            <FilledButtonM
                style={{ marginTop: 10, width }}
                loading={props.sending}
                onPress={() => sendCode(next.other_channel)}
                label="Recevoir ce code"
            />
            <ErrorTextM errorData={props.sendError} style={styles.center} />
            <TextButtonM
                style={{ marginTop: 16 }}
                onPress={() => setStep("new_child")}
                label={`Continuer sans retrouver ${firstname}`}
            />
        </>
    )

    const renderNewChild = () => (
        <>
            <TextM
                fontSize={22}
                fontWeight="ExtraBold"
                color={theme.colors.primary}
                style={styles.center}
                wrap
            >
                Ajouter {firstname} à votre famille ?
            </TextM>
            <TextM style={{ ...styles.lead, marginTop: 10 }} wrap>
                Nous n&apos;avons pas trouvé {firstname} parmi vos enfants.
            </TextM>
            <FilledButtonM
                style={{ marginTop: 10, width }}
                loading={props.resolving}
                onPress={() => resolve(null)}
                label={`Oui, ajouter ${firstname}`}
                wrapText
            />
            {next?.children?.length > 0 && (
                <TextButtonM
                    style={{ marginTop: 16 }}
                    onPress={() => setStep("pick")}
                    label="Non, c'est un autre de mes enfants"
                />
            )}
            <ErrorTextM errorData={props.resolveError} style={styles.center} />
        </>
    )

    const renderPick = () => (
        <>
            <TextM style={styles.lead} wrap>
                Pour quel enfant remplissez-vous ce formulaire ?
            </TextM>
            {next?.children?.map((child) => (
                <ChannelRow
                    key={child.id}
                    icon="user-graduate"
                    title={`${child.firstname} ${child.lastname ?? ""}`.trim()}
                    disabled={props.resolving}
                    onPress={() => resolve(child.id)}
                />
            ))}
            <ErrorTextM errorData={props.resolveError} style={styles.center} />
            <TextButtonM
                style={{ marginTop: 16 }}
                color={theme.colors.grey}
                onPress={() => setStep("new_child")}
                label="Retour"
            />
        </>
    )

    const renderSubscribed = () => (
        <>
            <TextM
                fontSize={22}
                fontWeight="ExtraBold"
                color={theme.colors.primary}
                style={styles.center}
                wrap
            >
                {isInscription
                    ? `${landing?.firstname} a déjà un abonnement en cours`
                    : `${landing?.firstname} suit déjà ses cours avec nous`}
            </TextM>
            <TextM style={{ ...styles.lead, marginTop: 10 }} wrap>
                {isInscription
                    ? "Son abonnement se gère dans l'espace parent."
                    : "Ses cours et ses comptes rendus sont dans son espace."}
            </TextM>
            <FilledButtonM
                style={{ marginTop: 10, width }}
                loading={goingHome}
                onPress={goToSpace}
                label={
                    isInscription
                        ? "Voir son abonnement"
                        : childSpaceLabel(landing?.firstname)
                }
                wrapText
            />
        </>
    )

    const renderStep = {
        choose: renderChoose,
        code: renderCode,
        password: renderPassword,
        other_channel: renderOtherChannel,
        new_child: renderNewChild,
        pick: renderPick,
        subscribed: renderSubscribed,
    }[step]

    return (
        <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={styles.scroll}
        >
            <View style={{ width, alignItems: "center" }}>
                <Image source={logo} style={styles.logo} resizeMode="contain" />
                {["choose", "code", "password", "other_channel"].includes(
                    step
                ) && (
                    <TextM
                        fontSize={26}
                        fontWeight="ExtraBold"
                        color={theme.colors.primary}
                        style={{ ...styles.center, marginBottom: 12 }}
                        wrap
                    >
                        On vous connaît déjà
                    </TextM>
                )}
                {renderStep()}
            </View>
        </ScrollView>
    )
}

const styles = StyleSheet.create({
    scroll: {
        flexGrow: 1,
        alignItems: "center",
        paddingHorizontal: 20,
        paddingTop: 40,
        paddingBottom: 60,
        backgroundColor: theme.colors.whiteAlmost,
    },
    logo: {
        width: 120,
        height: 40,
        marginBottom: 24,
    },
    center: {
        textAlign: "center",
    },
    lead: {
        textAlign: "center",
        marginBottom: 20,
        lineHeight: 22,
    },
    sectionTitle: {
        alignSelf: "flex-start",
        marginBottom: 10,
    },
    channelRow: {
        width: "100%",
        borderRadius: 15,
        marginBottom: 12,
        backgroundColor: theme.colors.white,
        borderWidth: 2,
        borderColor: theme.colors.primaryLighter,
    },
    channelRowContent: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        paddingVertical: 16,
        paddingHorizontal: 18,
    },
})

const mapStateToProps = (state) => ({
    sending: state.api.sendRecognitionCode?.request,
    sendError: state.api.sendRecognitionCode?.error,
    verifying: state.api.verifyRecognition?.request,
    verifyError: state.api.verifyRecognition?.error,
    resolving: state.api.resolveRecognition?.request,
    resolveError: state.api.resolveRecognition?.error,
    skipping: state.api.skipRecognition?.request,
    skipError: state.api.skipRecognition?.error,
    sessionToken: state.data.session?.object?.token,
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    switchAccount,
    setPendingDeepLink,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FastTrackRecognitionFlow)
